import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingAccountDA } from '../../services/admin/accounting.account.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

export interface AppModalAccountRenameProps {
  companyForUser: Company | null,
  account: AccountingAccount | null,
  loadFunction: Function
};

let errorResponse: Error;

const AppModalAccountRename: React.FunctionComponent<AppModalAccountRenameProps> = ({companyForUser, account, loadFunction}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [nameFr, setNameFr] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [nameDe, setNameDe] = useState('')
  const [nameIt, setNameIt] = useState('')

  useEffect(() => {
    if (account) {
      setNameFr(account.name?.fr ?? '')
      setNameEn(account.name?.en ?? '')
      setNameDe(account.name?.de ?? '')
      setNameIt(account.name?.it ?? '')
    }
  }, [account])

  const submitAccountRename = async () => {
    setLoadIndicator('on')

    if (!companyForUser || !account) {
      setLoadIndicator('off')
      return
    }

    const payload: { fr?: string, en?: string, de?: string, it?: string } = {}
    if (nameFr !== (account.name?.fr ?? '')) payload.fr = nameFr
    if (nameEn !== (account.name?.en ?? '')) payload.en = nameEn
    if (nameDe !== (account.name?.de ?? '')) payload.de = nameDe
    if (nameIt !== (account.name?.it ?? '')) payload.it = nameIt

    if (Object.keys(payload).length === 0) {
      executeHideModal()
      setLoadIndicator('off')
      return
    }

    await AccountingAccountDA.putAccountingAccountName(
      companyForUser.id,
      account.id,
      payload
    ).then((response) => {
      if (response.status === 200) {
        loadFunction(companyForUser.id)

        GlobalSwal.fire({
          title: lang.labels.accountRenamedSuccessfully,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then(() => {
          executeHideModal()
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const executeHideModal = () => {
    modalHide('modal-account-rename')
  }

  return (
    <div id="modal-account-rename" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.renameAccount}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              { account &&
                <div className="mb-5">
                  <span className="fw-bold text-muted">{lang.labels.code}:</span>
                  <span className="ms-2 fw-bolder">{account.code}</span>
                </div>
              }
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameFr}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameFr} onChange={(e) => setNameFr(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameEn}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameDe}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameDe} onChange={(e) => setNameDe(e.target.value)} />
                </div>
              </div>
              <div className="row mb-4">
                <label className="col-lg-3 col-form-label">{lang.labels.nameIt}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" autoComplete="off" value={nameIt} onChange={(e) => setNameIt(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountRename}>
              <span className="indicator-label">{lang.labels.save}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalAccountRename;
